import React, { useState, useEffect, useRef } from 'react';
import { API_BASE_URL } from '../../config';
import Cropper from 'react-easy-crop';

function BankStep({ formData, handleInputChange, focusedField, isAnimating, handlePrevStep, handleNextStep, isReadOnly }) {
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [apiError, setApiError] = useState(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [showChequeModal, setShowChequeModal] = useState(false);
  const [chequePreview, setChequePreview] = useState(null);
  const [isUploadingCheque, setIsUploadingCheque] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const fileInputRef = useRef(null);
  const dataFetchedRef = useRef(false);
  const [gstVerified] = useState(false);

  // Validation patterns
  const validationPatterns = {
    ifsc: /^[A-Z]{4}0[A-Z0-9]{6}$/, // Basic IFSC code pattern
    accountNumber: /^[0-9]{9,18}$/ // Basic account number pattern (9-18 digits)
  };

  useEffect(() => {
    // Only fetch data once
    if (dataFetchedRef.current) return;

    const fetchBankDetails = async () => {
      try {
        setIsLoading(true);
        setApiError(null);
        
        // Get auth details from localStorage or context
        const auth_key = localStorage.getItem('authKey');
        const user_id = localStorage.getItem('userId');

        if (!auth_key || !user_id) {
          throw new Error('Authentication credentials not found. Please log in again.');
        }

        const response = await fetch(`${API_BASE_URL}/brands/onboarding/bank-details/fetch`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ auth_key, user_id }),
        });

        const data = await response.json();

        if (response.ok && data.status === 'success') {
          // Update form data with fetched information
          if (data.bank_details) {
            // Call the parent component's handleInputChange with each field
            Object.entries(data.bank_details).forEach(([key, value]) => {
              if (value && formData.hasOwnProperty(key)) {
                handleInputChange({
                  target: {
                    name: key,
                    value: value
                  }
                }, { isFromApi: true });
              }
            });
          }
        } else {
          // If 404 is returned, it means bank details don't exist yet which is fine
          if (response.status !== 404) {
            throw new Error(data.message || 'Failed to fetch bank information');
          }
        }
        
        // Mark as fetched to prevent further fetches
        dataFetchedRef.current = true;
      } catch (error) {
        console.error('Error fetching bank info:', error);
        setApiError(error.message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchBankDetails();
  }, [formData, handleInputChange]);

  useEffect(() => {
    if (gstVerified) {
      // existing logic
    }
  }, [gstVerified]);

  // Validation error messages
  const errorMessages = {
    accountHolderName: 'Please enter the account holder name',
    accountNumber: 'Please enter a valid account number (9-18 digits)',
    ifsc: 'Please enter a valid IFSC code (e.g., SBIN0123456)',
    bankName: 'Please enter the bank name',
    accountType: 'Please select an account type',
    cancelledCheque: 'Please upload a cancelled cheque'
  };

  // Field level validation
  const validateField = (name, value) => {
    if (!value || value.trim() === '') {
      return errorMessages[name] || 'This field is required';
    }
    
    if (validationPatterns[name] && !validationPatterns[name].test(value)) {
      return errorMessages[name];
    }
    
    return '';
  };

  // Enhanced form validation
  const validateForm = () => {
    const newErrors = {};
    let isValid = true;
    
    // Validate required fields
    ['accountHolderName', 'accountNumber', 'ifsc', 'bankName', 'accountType', 'cancelledCheque'].forEach(field => {
      const error = validateField(field, formData[field]);
      if (error) {
        newErrors[field] = error;
        isValid = false;
      }
    });
    
    setErrors(newErrors);
    return isValid;
  };

  // Enhanced input change handler with real-time validation
  const handleFormInputChange = (e) => {
    const { name, value } = e.target;
    
    // Transform IFSC to uppercase
    let transformedValue = value;
    if (name === 'ifsc') {
      transformedValue = value.toUpperCase();
    }
    
    // Call the parent's handleInputChange function with transformed value
    handleInputChange({
      target: {
        name,
        value: transformedValue
      }
    });
    
    // Clear error when user starts typing
    setErrors(prev => {
      const updatedErrors = { ...prev };
      
      // Validate the current field
      const error = validateField(name, transformedValue);
      if (error) {
        updatedErrors[name] = error;
      } else {
        delete updatedErrors[name];
      }
      
      return updatedErrors;
    });
  };

  // Cheque Upload Functions
  const handleChequeUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleChequeFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file is an image
    if (!file.type.startsWith('image/')) {
      setApiError('Please select a valid image file');
      return;
    }

    // Create a preview of the image
    const imageUrl = URL.createObjectURL(file);
    setChequePreview(imageUrl);
    setShowChequeModal(true);
  };

  const handleCropComplete = (croppedImage) => {
    // This function will be called after cropping is complete
    setChequePreview(croppedImage);
    setShowChequeModal(false);
  };

  const handleCropCancel = () => {
    // Reset cheque state
    setChequePreview(null);
    setShowChequeModal(false);
  };

  const uploadCheque = async (imageFile) => {
    try {
      setIsUploadingCheque(true);
      setApiError(null);
      setUploadProgress(0);

      // Get auth details from localStorage
      const auth_key = localStorage.getItem('authKey');
      const user_id = localStorage.getItem('userId');

      if (!auth_key || !user_id) {
        throw new Error('Authentication credentials not found. Please log in again.');
      }

      // Create form data - use cheque_image to match backend parameter name
      const formData = new FormData();
      formData.append('cheque_image', imageFile, imageFile.name);
      formData.append('auth_key', auth_key);
      formData.append('user_id', user_id);

      // Use XMLHttpRequest for progress tracking
      const xhr = new XMLHttpRequest();
      
      // Setup progress tracking
      xhr.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable) {
          const progress = Math.round((event.loaded / event.total) * 100);
          setUploadProgress(progress);
        }
      });
      
      // Create a promise to handle the XHR response
      const uploadPromise = new Promise((resolve, reject) => {
        xhr.onload = function() {
          if (xhr.status >= 200 && xhr.status < 300) {
            try {
              const response = JSON.parse(xhr.responseText);
              console.log('Cheque upload response:', response); // Debug log
              resolve(response);
            } catch (error) {
              console.error('Error parsing server response:', error);
              reject(new Error('Invalid response from server'));
            }
          } else {
            console.error('Cheque upload failed with status:', xhr.status, xhr.statusText);
            // Try to parse error response if possible
            try {
              const errorResponse = JSON.parse(xhr.responseText);
              reject(new Error(errorResponse.message || errorResponse.detail || 'Upload failed: ' + xhr.statusText));
            } catch {
              reject(new Error('Upload failed: ' + xhr.statusText));
            }
          }
        };
        
        xhr.onerror = function() {
          console.error('Network error during cheque upload');
          reject(new Error('Network error during upload'));
        };
      });
      
      // Send the request
      xhr.open('POST', `${API_BASE_URL}/brands/onboarding/cheque/upload`, true);
      // Do NOT set Content-Type header - let the browser set it with the correct boundary string for multipart/form-data
      console.log('Sending cheque upload request to:', `${API_BASE_URL}/brands/onboarding/cheque/upload`);
      xhr.send(formData);
      
      const response = await uploadPromise;
      
      if (response.status === 'success') {
        // Get the cheque URL from the response
        const chequeUrl = response.data?.cheque_url || response.cheque_url || response.data?.url || response.url;
        
        console.log('Cheque URL from response:', chequeUrl); // Debug log
        
        if (!chequeUrl) {
          console.error('No cheque URL in response:', response);
          throw new Error('No cheque URL returned from server');
        }
        
        // Clear any existing cheque error
        setErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors.cancelledCheque;
          return newErrors;
        });
        
        // Update form data with cheque URL
        handleInputChange({
          target: {
            name: 'cancelledCheque',
            value: chequeUrl
          }
        });
        
        console.log('Form data after cheque update:', formData); // Debug log
        
        setSaveSuccess(true);
        setTimeout(() => setSaveSuccess(false), 3000);
      } else {
        console.error('Cheque upload returned non-success status:', response);
        throw new Error(response.message || 'Failed to upload cancelled cheque');
      }
    } catch (error) {
      console.error('Error uploading cheque:', error);
      setApiError(error.message);
    } finally {
      setIsUploadingCheque(false);
      setUploadProgress(0);
    }
  };

  const handleSaveProgress = async () => {
    if (isReadOnly) return;
    
    setIsSubmitting(true);
    setSaveSuccess(false);
    setApiError(null);

    try {
      // Get auth details from localStorage or context
      const auth_key = localStorage.getItem('authKey');
      const user_id = localStorage.getItem('userId');

      if (!auth_key || !user_id) {
        throw new Error('Authentication credentials not found. Please log in again.');
      }

      // Check if cancelled cheque is provided before saving
      if (!formData.cancelledCheque || formData.cancelledCheque.trim() === '') {
        setErrors(prev => ({
          ...prev,
          cancelledCheque: errorMessages.cancelledCheque
        }));
        throw new Error('Please upload a cancelled cheque before saving');
      }

      const payload = {
        ...formData,
        auth_key,
        user_id
      };

      console.log('Payload:', payload);

      const response = await fetch(`${API_BASE_URL}/brands/onboarding/bank-details`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Something went wrong');
      }

      // If successful, show success message
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000); // Hide after 3 seconds
    } catch (error) {
      setApiError(error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleNext = async (e) => {
    if (isReadOnly) {
      handleNextStep();
      return;
    }
    
    e.preventDefault();
    if (validateForm()) {
      setIsSubmitting(true);
      setApiError(null);

      try {
        // Get auth details from localStorage or context
        const auth_key = localStorage.getItem('authKey');
        const user_id = localStorage.getItem('userId');

        if (!auth_key || !user_id) {
          throw new Error('Authentication credentials not found. Please log in again.');
        }

        const payload = {
          ...formData,
          auth_key,
          user_id
        };

        const response = await fetch(`${API_BASE_URL}/brands/onboarding/bank-details`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
        });

        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.message || 'Something went wrong');
        }

        // If successful, move to next step
        handleNextStep();
      } catch (error) {
        setApiError(error.message);
      } finally {
        setIsSubmitting(false);
      }
    } else {
      // Focus the first field with an error
      const firstErrorField = Object.keys(errors)[0];
      if (firstErrorField) {
        document.querySelector(`[name="${firstErrorField}"]`)?.focus();
      }
    }
  };

  return (
    <div className="onboarding-section bank-details">
      <div className="bank-container">
        <h1 className="section-title">Bank Account Details</h1>

        {apiError && (
          <div className="api-error-message">
            {apiError}
          </div>
        )}

        {saveSuccess && (
          <div className="success-message">
            Your progress has been saved successfully!
          </div>
        )}

        {isLoading ? (
          <div className="loading-state">
            <p>Loading your information...</p>
          </div>
        ) : (
          <>
            <section className="bank-account-info">
              <div className="form-row">
                <div className={`form-group ${focusedField === 'accountHolderName' ? 'focused' : ''}`}>
                  <label>Account Holder Name<span className="required"></span></label>
                  <input
                    type="text"
                    name="accountHolderName"
                    value={formData.accountHolderName}
                    onChange={handleInputChange}
                    placeholder="Account Holder Name as per Bank Records"
                    readOnly={isReadOnly}
                  />
                  {errors.accountHolderName && <span className="error-message">{errors.accountHolderName}</span>}
                </div>
                <div className={`form-group ${focusedField === 'accountNumber' ? 'focused' : ''}`}>
                  <label>Account Number<span className="required"></span></label>
                  <input
                    type="text"
                    name="accountNumber"
                    value={formData.accountNumber}
                    onChange={handleFormInputChange}
                    placeholder="Bank Account Number"
                    readOnly={isReadOnly}
                  />
                  {errors.accountNumber && <span className="error-message">{errors.accountNumber}</span>}
                </div>
              </div>
              
              <div className="form-row">
                <div className={`form-group ${focusedField === 'ifsc' ? 'focused' : ''}`}>
                  <label>IFSC Code<span className="required"></span></label>
                  <input
                    type="text"
                    name="ifsc"
                    value={formData.ifsc}
                    onChange={handleFormInputChange}
                    placeholder="IFSC Code (e.g. SBIN0001234)"
                    readOnly={isReadOnly}
                  />
                  {errors.ifsc && <span className="error-message">{errors.ifsc}</span>}
                </div>
                <div className={`form-group ${focusedField === 'bankName' ? 'focused' : ''}`}>
                  <label>Bank Name<span className="required"></span></label>
                  <input
                    type="text"
                    name="bankName"
                    value={formData.bankName}
                    onChange={handleInputChange}
                    placeholder="Bank Name"
                    disabled={isReadOnly}
                  />
                  {errors.bankName && <span className="error-message">{errors.bankName}</span>}
                </div>
              </div>
              
              <div className="form-row">
                <div className={`form-group ${focusedField === 'accountType' ? 'focused' : ''}`}>
                  <label>Account Type<span className="required"></span></label>
                  <select
                    name="accountType"
                    value={formData.accountType}
                    onChange={handleInputChange}
                    disabled={isReadOnly}
                  >
                    <option value="">Select Account Type</option>
                    <option value="savings">Savings</option>
                    <option value="current">Current</option>
                  </select>
                  {errors.accountType && <span className="error-message">{errors.accountType}</span>}
                </div>
                <div className={`form-group ${focusedField === 'cancelledCheque' ? 'focused' : ''}`}>
                  <label>Cancelled Cheque<span className="required"></span></label>
                  <div className="cheque-input" style={{ 
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    marginBottom: '10px'
                  }}>
                    <input
                      type="text"
                      name="cancelledCheque"
                      value={formData.cancelledCheque || ''}
                      onChange={handleFormInputChange}
                      placeholder="Upload cancelled cheque"
                      readOnly={isReadOnly}
                      style={{ 
                        flex: 1,
                        cursor: 'default',
                        backgroundColor: formData.cancelledCheque ? '#f0f8ff' : '',
                        border: formData.cancelledCheque ? '1px solid #4CAF50' : '1px solid #ddd'
                      }}
                    />
                    <button 
                      type="button"
                      className="upload-button"
                      onClick={handleChequeUploadClick}
                      disabled={isReadOnly || isUploadingCheque}
                      style={{
                        minWidth: isUploadingCheque ? '130px' : '80px',
                        backgroundColor: formData.cancelledCheque ? '#4CAF50' : '#2196F3',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        padding: '8px 15px',
                        cursor: isReadOnly ? 'default' : isUploadingCheque ? 'default' : 'pointer',
                        transition: 'all 0.2s ease'
                      }}
                    >
                      {isReadOnly ? 'View' : isUploadingCheque ? `Uploading... ${uploadProgress}%` : 
                       formData.cancelledCheque ? 'Change' : 'Upload'}
                    </button>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleChequeFileSelect}
                      style={{ display: 'none' }}
                    />
                  </div>
                  {formData.cancelledCheque && (
                    <div className="cheque-preview" style={{
                      border: '1px solid #eee',
                      borderRadius: '4px',
                      padding: '10px',
                      backgroundColor: '#f9f9f9',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center'
                    }}>
                      <img 
                        src={formData.cancelledCheque} 
                        alt="Cancelled Cheque" 
                        style={{ maxHeight: '100px', maxWidth: '100%' }}
                      />
                      <div style={{ 
                        marginTop: '5px', 
                        color: '#4CAF50', 
                        fontSize: '12px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '5px'
                      }}>
                        <span style={{ fontSize: '16px' }}>✓</span>
                        <span>Cancelled cheque uploaded successfully</span>
                      </div>
                    </div>
                  )}
                  {errors.cancelledCheque && (
                    <span className="error-message" style={{ 
                      display: 'block', 
                      marginTop: '5px',
                      color: '#f44336',
                      fontSize: '14px',
                      fontWeight: '500'
                    }}>
                      {errors.cancelledCheque}
                    </span>
                  )}
                </div>
              </div>
            </section>

            <div className="form-actions">
              {!isReadOnly && (
                <button 
                  className="save-progress-button"
                  onClick={handleSaveProgress}
                  disabled={isSubmitting || isLoading}
                >
                  {isSubmitting ? 'Saving...' : 'Save Progress'}
                </button>
              )}
              <div className="navigation-buttons">
                <button 
                  className="back-button"
                  onClick={handlePrevStep}
                  disabled={isSubmitting || isLoading}
                >
                  Back
                </button>
                <button 
                  className="next-button"
                  onClick={handleNext}
                  disabled={isSubmitting || isAnimating || isLoading}
                >
                  {isSubmitting ? 'Submitting...' : 'Next'}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
      
      {/* Cheque Cropping Modal */}
      {showChequeModal && chequePreview && (
        <ChequeCropModal
          image={chequePreview}
          onCropComplete={handleCropComplete}
          onCancel={handleCropCancel}
          uploadCheque={uploadCheque}
        />
      )}
    </div>
  );
}

// Cheque Cropping Modal Component
function ChequeCropModal({ image, onCropComplete, onCancel, uploadCheque }) {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  
  const onCropChange = (newCrop) => {
    setCrop(newCrop);
  };

  const onZoomChange = (newZoom) => {
    setZoom(newZoom);
  };

  const handleCropComplete = (croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  };

  const createCroppedImage = async () => {
    try {
      const croppedImage = await getCroppedImg(image, croppedAreaPixels);
      // Upload the cropped image
      await uploadCheque(croppedImage);
      onCropComplete(URL.createObjectURL(croppedImage)); // For preview
    } catch (e) {
      console.error('Error processing cropped image:', e);
    }
  };

  // Function to create the cropped image
  const getCroppedImg = (imageSrc, pixelCrop) => {
    return new Promise((resolve, reject) => {
      const image = new Image();
      image.src = imageSrc;
      
      image.onload = () => {
        // Create canvas
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        
        // Set canvas dimensions to cropped area size
        canvas.width = pixelCrop.width;
        canvas.height = pixelCrop.height;
        
        // Draw the cropped image
        ctx.drawImage(
          image,
          pixelCrop.x,
          pixelCrop.y,
          pixelCrop.width,
          pixelCrop.height,
          0,
          0,
          pixelCrop.width,
          pixelCrop.height
        );
        
        // Convert canvas to blob
        canvas.toBlob((blob) => {
          if (!blob) {
            reject(new Error('Canvas is empty'));
            return;
          }
          // Create a File object from the blob
          const croppedFile = new File([blob], 'cancelled_cheque.png', { type: 'image/png' });
          resolve(croppedFile);
        }, 'image/png');
      };

      image.onerror = () => {
        reject(new Error('Error loading image'));
      };
    });
  };

  return (
    <div className="modal-overlay" style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.7)',
      zIndex: 1000,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center'
    }}>
      <div className="crop-container" style={{
        width: '90%',
        maxWidth: '500px',
        backgroundColor: 'white',
        borderRadius: '8px',
        overflow: 'hidden',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
      }}>
        <div className="crop-header" style={{
          padding: '16px',
          borderBottom: '1px solid #eee',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <h3 style={{ margin: 0 }}>Crop Cancelled Cheque</h3>
          <button 
            onClick={onCancel}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '20px',
              cursor: 'pointer'
            }}
          >
            ×
          </button>
        </div>
        
        <div style={{ 
          position: 'relative',
          width: '100%',
          height: '300px',
          backgroundColor: '#f0f0f0'
        }}>
          <Cropper
            image={image}
            crop={crop}
            zoom={zoom}
            aspect={16/9}
            onCropChange={onCropChange}
            onZoomChange={onZoomChange}
            onCropComplete={handleCropComplete}
          />
        </div>

        <div style={{
          padding: '16px',
          borderTop: '1px solid #eee'
        }}>
          <div style={{ marginBottom: '12px' }}>
            <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px' }}>
              Zoom
            </label>
            <input
              type="range"
              value={zoom}
              min={1}
              max={3}
              step={0.1}
              onChange={(e) => onZoomChange(Number(e.target.value))}
              style={{ width: '100%' }}
            />
          </div>
        </div>
        
        <div style={{
          padding: '16px',
          display: 'flex',
          justifyContent: 'space-between',
          borderTop: '1px solid #eee'
        }}>
          <button 
            onClick={onCancel}
            style={{
              padding: '8px 16px',
              border: '1px solid #ddd',
              borderRadius: '4px',
              backgroundColor: 'white',
              cursor: 'pointer'
            }}
          >
            Cancel
          </button>
          <button 
            onClick={createCroppedImage}
            style={{
              padding: '8px 16px',
              border: 'none',
              borderRadius: '4px',
              backgroundColor: '#2196F3',
              color: 'white',
              cursor: 'pointer'
            }}
          >
            Apply
          </button>
        </div>
      </div>
    </div>
  );
}

export default BankStep; 